import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { useRecoilValue } from 'recoil';

import { updateUserPreferences } from 'api/Preferences';

import WIZARD_CODES from 'lib/constants/wizard/wizard_codes';
import { iaRa } from 'lib/helpers/utility';
import { usePracticeSupportSettings } from 'lib/hooks/queries/practice/usePracticeSupportSettings';
import { useUserPreferences } from 'lib/hooks/queries/user/useUserPreferences';
import { useGetWizardInstance } from 'lib/hooks/queries/wizard/useGetWizardInstance';

import adminState from 'components/admin/lib/adminState';
import state from 'components/state';

import Loader from '../Loader/Loader';
import Stepper from '../Stepper/Stepper';
import { COMPLETED_STATUSES, IMPORT_FROM_ORG_CODE } from '../Stepper/lib/constants';
import CustomTour from '../Tour/CustomTour';

import Intro from './components/Intro';
import { tourSteps } from './lib/helpers';

const ONBOARDING_TOUR_COMPLETED_KEY = 'onboarding_tour_completed';

const OnboardingWizard = () => {
  const user_state = useRecoilValue(state.userState);
  const admin_state = useRecoilValue(adminState.current_admin);
  const [showIntro, setShowIntro] = useState(user_state?.authenticated);
  const [showTour, setShowTour] = useState(false);
  const { practice } = useOutletContext();
  const navigate = useNavigate();

  const isAdmin = !user_state.authenticated && admin_state?.logged_in;

  const { data: preferencesData, refetch: refetchPreferences } = useUserPreferences({
    params: { name: ONBOARDING_TOUR_COMPLETED_KEY },
    dependencies: [ONBOARDING_TOUR_COMPLETED_KEY],
    options: { enabled: user_state?.authenticated }
  });

  const { data, isLoading } = useGetWizardInstance({
    params: { practice_id: practice?.id, wizard_kind_code: WIZARD_CODES.ONBOARDING },
    dependencies: [practice?.id],
    options: {
      refetchOnMount: true,
      enabled: !!practice?.id
    }
  });

  const wizardInstanceId = data?.data?.instance?.id;

  const { data: supportData } = usePracticeSupportSettings({
    params: { practiceId: practice?.id },
    dependencies: [practice?.id],
    options: { enabled: !!practice?.id }
  });

  const consultant = supportData?.practiceSupport?.consultant;

  const wizardSteps = data?.data?.steps;

  const hiddenSubstepCodes = useMemo(() => {
    const codes = new Set();
    if (!practice?.organization?.id) {
      const allSubsteps = (wizardSteps ?? []).flatMap((s) => s.substeps ?? []);
      const importFromOrg = allSubsteps.find((sub) => sub.code === IMPORT_FROM_ORG_CODE);
      if (importFromOrg && COMPLETED_STATUSES.includes(importFromOrg.status)) {
        codes.add(IMPORT_FROM_ORG_CODE);
      }
    }
    return codes;
  }, [wizardSteps, practice?.organization?.id]);

  const seenInstanceIds = useMemo(
    () => iaRa(preferencesData?.preferences?.value?.seenInstances),
    [preferencesData]
  );

  const hasSeenTour = useMemo(
    () => !!wizardInstanceId && seenInstanceIds.includes(wizardInstanceId),
    [wizardInstanceId, seenInstanceIds]
  );

  const startTourIfUnseen = useCallback(() => {
    if (wizardInstanceId && !hasSeenTour) {
      setShowTour(true);
    }
  }, [wizardInstanceId, hasSeenTour]);

  const handleTourClose = useCallback(async () => {
    setShowTour(false);
    if (wizardInstanceId && !hasSeenTour) {
      const updatedValue = { seenInstances: [...seenInstanceIds, wizardInstanceId] };
      await updateUserPreferences(navigate, {
        name: ONBOARDING_TOUR_COMPLETED_KEY,
        value: updatedValue
      });
      refetchPreferences();
    }
  }, [wizardInstanceId, hasSeenTour, seenInstanceIds, navigate, refetchPreferences]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      {data?.data?.instance?.assigned_to === user_state.id && showIntro && (
        <Intro
          title="Myriad Health"
          description="The most comprehensive practice management system ever created."
          consultant={consultant}
          onEndIntro={() => {
            setShowIntro(false);
            if (user_state?.authenticated) {
              startTourIfUnseen();
            }
          }}
          onSkip={() => navigate('/portal')}
        />
      )}
      {showTour && <CustomTour steps={tourSteps} onClose={handleTourClose} />}
      <Stepper
        wizardKind="ONBOARDING"
        isAdmin={isAdmin}
        wizardState={data?.data}
        practice={practice}
        hiddenSubstepCodes={hiddenSubstepCodes}
        onShowTour={startTourIfUnseen}
      />
    </>
  );
};

export default OnboardingWizard;
